import React, { useEffect, useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Box, 
  IconButton, 
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ModifierIcon from '@mui/icons-material/LocalOffer';
import apiService from '../services/api';

interface Modifier {
  id: string;
  code: string;
  description: string;
  category: string;
  meaning: string;
  typicalUse: string;
}

const modifierCategories = [
  'Component Splits',
  'Location & Laterality',
  'Distinct Services',
  'Repeat & Multiple Sampling',
  'Reduced / Altered Services',
  'Reference Labs & Outsourcing',
  'Telehealth',
  'Other Common'
];



const defaultForm: Partial<Modifier> = { 
  code: '', 
  description: '', 
  category: '', 
  meaning: '', 
  typicalUse: '' 
};

const Modifiers: React.FC = () => {
  const [modifiers, setModifiers] = useState<Modifier[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<Modifier | null>(null);
  const [form, setForm] = useState<Partial<Modifier>>(defaultForm);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const fetchModifiers = async (pageNum = 0, pageSize = 20, searchTerm = '', category = '') => {
    setLoading(true);
    try {
      const data = await apiService.getModifiers(pageNum + 1, pageSize, searchTerm || undefined, category || undefined);
      setModifiers(data.modifiers || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching modifiers:', error);
      setModifiers([]);
      setTotal(0);
    }
    setLoading(false);
  };

  // Debounced search effect
  useEffect(() => { 
    const timeoutId = setTimeout(() => {
      fetchModifiers(page, rowsPerPage, search, categoryFilter);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, search, categoryFilter]);

  const handleOpenDialog = (modifier?: Modifier) => {
    setEditing(modifier || null);
    setForm(modifier ? { ...modifier } : defaultForm);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { 
    setOpenDialog(false); 
    setEditing(null); 
    setForm(defaultForm); 
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await apiService.updateModifier(editing.id, form);
      } else {
        await apiService.createModifier(form);
      }
      handleCloseDialog();
      fetchModifiers(page, rowsPerPage, search, categoryFilter);
    } catch (error) {
      console.error('Error saving modifier:', error);
      alert('Failed to save modifier. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this modifier?')) return;
    try {
      await apiService.deleteModifier(id);
      fetchModifiers(page, rowsPerPage, search, categoryFilter);
    } catch (error) {
      console.error('Error deleting modifier:', error);
      alert('Failed to delete modifier. Please try again.');
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(0);
  };

  const handleCategoryFilterChange = (event: any) => {
    setCategoryFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'Component Splits': '#1976d2',
      'Location & Laterality': '#388e3c',
      'Distinct Services': '#f57c00',
      'Repeat & Multiple Sampling': '#7b1fa2',
      'Reduced / Altered Services': '#d32f2f',
      'Reference Labs & Outsourcing': '#0288d1',
      'Telehealth': '#5d4037',
      'Other Common': '#616161'
    };
    return colors[category as keyof typeof colors] || '#616161';
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
            CPT/HCPCS Modifiers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage two-character modifiers that qualify how a test or procedure was performed
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
        >
          Add Modifier
        </Button>
      </Box>

      {/* Search and Filter Box */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by code, description, meaning, or typical use..."
                value={search}
                onChange={handleSearchChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                        sx={{ color: '#666' }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Filter by Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Filter by Category"
                  onChange={handleCategoryFilterChange}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {modifierCategories.map(category => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Modifiers Table */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Code
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Typical Use in LIS
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        Loading modifiers...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please wait while we fetch the data
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : modifiers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <ModifierIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {search || categoryFilter ? 'No modifiers found' : 'No modifiers available'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {search || categoryFilter
                          ? 'Try adjusting your search terms or filters to see more results.'
                          : 'Get started by adding your first modifier.'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                modifiers.map((modifier) => (
                  <TableRow key={modifier.id} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" alignItems="center">
                        <Avatar 
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            mr: 2, 
                            backgroundColor: getCategoryColor(modifier.category),
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}
                        >
                          {modifier.code}
                        </Avatar>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {modifier.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {modifier.meaning}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip 
                        label={modifier.category} 
                        size="small" 
                        sx={{ 
                          backgroundColor: getCategoryColor(modifier.category),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, maxWidth: '300px' }}>
                      <Typography variant="body2" color="text.secondary">
                        {modifier.typicalUse}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(modifier)}
                          sx={{ 
                            color: '#1976d2',
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(modifier.id)}
                          sx={{ 
                            color: '#d32f2f',
                            '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editing ? 'Edit Modifier' : 'Add New Modifier'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Modifier Code"
                name="code"
                value={form.code || ''}
                onChange={handleFormChange}
                required
                inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                helperText="2-character modifier code (e.g., 26, TC, 50)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.category || ''}
                  label="Category"
                  onChange={(e) => handleSelectChange('category', e.target.value)}
                >
                  {modifierCategories.map(category => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={form.description || ''}
                onChange={handleFormChange}
                required
                helperText="Brief description of the modifier"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Meaning"
                name="meaning"
                value={form.meaning || ''}
                onChange={handleFormChange}
                required
                helperText="What this modifier indicates"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Typical Use in LIS"
                name="typicalUse"
                value={form.typicalUse || ''}
                onChange={handleFormChange}
                multiline
                rows={3}
                required
                helperText="How this modifier is typically used in laboratory settings"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Modifiers;